import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountingDb, supabase, sharedDb } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { extractPdf, type PdfPage } from '@/lib/gemini';
import { formatErr } from '@/utils/format-error';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface PartnerOption {
  id: string;
  name: string;
}

export function PdfUploadPage() {
  const { tenantId } = useAuth();
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [partnerId, setPartnerId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      const { data } = await sharedDb()
        .from('partners')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      setPartners((data ?? []) as PartnerOption[]);
    })();
  }, [tenantId]);

  const handleUpload = async () => {
    if (!tenantId || !file) return;
    setBusy(true);
    setError(null);
    try {
      // 1) Storage に PDF アップロード
      setProgress('PDFアップロード中...');
      const ym = new Date().toISOString().slice(0, 7);
      const path = `${tenantId}/${ym}/${crypto.randomUUID()}.pdf`;
      const { error: upErr } = await supabase.storage
        .from('accounting-pdfs')
        .upload(path, file, { contentType: 'application/pdf' });
      if (upErr) throw upErr;

      // 2) accounting.pdf_orders にレコード作成
      const { data: pdfOrder, error: pdfErr } = await accountingDb()
        .from('pdf_orders')
        .insert({
          tenant_id: tenantId,
          partner_id: partnerId || null,
          storage_path: path,
          original_file_name: file.name,
          file_size_bytes: file.size,
          status: 'uploaded',
          parser_used: 'pdfjs',
        })
        .select()
        .single();
      if (pdfErr) throw pdfErr;

      // 3) pdf.js でテキスト + 座標抽出
      setProgress('PDF解析中...');
      const buf = await file.arrayBuffer();
      const pdfDoc = await pdfjsLib.getDocument({ data: buf }).promise;
      const pages: PdfPage[] = [];
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const content = await page.getTextContent();
        const items = content.items.map((it) => {
          const item = it as unknown as { str: string; transform: number[]; width: number; height: number };
          // transform = [a, b, c, d, e, f] → e, f が x, y
          const x = item.transform[4] ?? 0;
          const y = item.transform[5] ?? 0;
          return {
            text: item.str,
            bbox: [x, y, item.width, item.height] as [number, number, number, number],
          };
        });
        const text = items.map((i) => i.text).join(' ');
        pages.push({ page_index: i - 1, text, items });
      }

      // 4) Edge Function で AI構造化
      setProgress('AI構造化中...');
      const res = await extractPdf({
        tenantId,
        ...(partnerId ? { partnerId } : {}),
        pdfOrderId: pdfOrder.id,
        pages,
      });

      navigate(`/extractions/${res.extraction_id}`);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  return (
    <div>
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <h1 className="text-sm font-semibold">PDF取込</h1>
      </header>
      <section className="space-y-4 p-4">
        <div>
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">取引先(任意)</label>
          <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
            <option value="">未指定</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">PDFファイル</label>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm"
          />
        </div>
        {error && <div className="rounded-md bg-rose-50 p-3 text-xs text-rose-700">{error}</div>}
        {progress && <div className="rounded-md bg-sky-50 p-3 text-xs text-sky-700">{progress}</div>}
        <button
          type="button"
          onClick={handleUpload}
          disabled={busy || !file}
          className="w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? '処理中...' : 'アップロードして AI 構造化'}
        </button>
      </section>
    </div>
  );
}
